import { api } from "./api";

/** An article card on /blog (GET /blog). */
export interface BlogCard {
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string | null;
  coverAlt: string;
  category: { name: string; slug: string } | null;
  author: string | null;
  publishedAt: string;
  readingMinutes: number;
  views: number;
  /** Language the texts are in (the site language when translated, else the original) */
  language: "en" | "fr";
  languages: ("en" | "fr")[];
}

/** GET /blog/:slug */
export interface BlogArticle extends BlogCard {
  content: string; // Markdown
  faq: { question: string; answer: string }[];
  tags: string[];
  /** { tag name: slug } for /blog/tag/:slug links */
  tagSlugs: Record<string, string>;
  /** Admin role of the author (super_admin, editor, writer) */
  authorRole: string | null;
  authorBio: string | null;
  /** SEO title through the blog's meta title template */
  metaTitle: string;
  cta: "vehicles" | "list" | "companies" | "none";
  seoTitle: string;
  seoDescription: string;
  canonicalUrl: string | null;
  noindex: boolean;
  nofollow: boolean;
  ogTitle: string;
  ogDescription: string;
  ogImage: string | null;
  updatedAt: string;
  related: BlogCard[];
}

/** GET /blog/sidebar */
export interface BlogSidebar {
  latest: BlogCard[];
  popular: BlogCard[];
  categories: { name: string; slug: string; count: number }[];
  archives: { month: string; count: number }[]; // "2026-10"
  tags: { tag: string; slug: string; count: number }[];
}

/** GET /blog/settings (texts in the site language) */
export interface BlogSettings {
  title: string;
  intro: string;
  metaTitleTemplate: string;
  perPage: number;
  showFaq: boolean;
}

export async function listArticles(
  params: { category?: string; q?: string; tag?: string; month?: string; page?: number; limit?: number } = {},
): Promise<{ items: BlogCard[]; total: number }> {
  const { data } = await api.get<{ items: BlogCard[]; total: number }>("/blog", { params: { limit: 12, ...params } });
  return data;
}

export async function getBlogSettings(): Promise<BlogSettings> {
  const { data } = await api.get<BlogSettings>("/blog/settings");
  return data;
}

/** Tag name for /blog/tag/:slug (null when unknown) */
export async function getTag(slug: string): Promise<{ name: string; slug: string } | null> {
  try {
    const { data } = await api.get<{ name: string; slug: string }>(`/blog/tags/${encodeURIComponent(slug)}`);
    return data;
  } catch {
    return null;
  }
}

export async function listBlogCategories(): Promise<{ name: string; slug: string }[]> {
  const { data } = await api.get<{ items: { name: string; slug: string }[] }>("/blog/categories");
  return data.items;
}

/** The article, or { redirect } when it moved (old address kept by a 301 redirect) */
export async function getArticle(slug: string): Promise<BlogArticle | { redirect: string }> {
  try {
    const { data } = await api.get<BlogArticle>(`/blog/${encodeURIComponent(slug)}`);
    return data;
  } catch (err) {
    const redirect = (err as { response?: { data?: { redirect?: string } } }).response?.data?.redirect;
    if (redirect) return { redirect };
    throw err;
  }
}

export async function getBlogSidebar(): Promise<BlogSidebar> {
  const { data } = await api.get<BlogSidebar>("/blog/sidebar");
  return data;
}

/** "2026-10" -> "October 2026" / "Octobre 2026" */
export const monthLabel = (month: string, locale: string) => {
  const [y, m] = month.split("-").map(Number);
  const s = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(locale, { month: "long", year: "numeric", timeZone: "UTC" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** Blog covers are 1200 × 630; the API also makes a 600 × 315 "…-thumb.webp" for cards. */
export const COVER_ASPECT = "aspect-[40/21]";
export const thumbOf = (url: string) => url.replace(/-1200\.webp$/, "-thumb.webp");

